import { useEffect, useRef } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { resolveDisplay, usePreferences } from '@/app/preferences';
import { RouteGate } from '@/app/RouteGate';
import { useGlobalHotkeys } from '@/app/use-hotkeys';
import { useSession } from '@/auth/session-context';
import { Spinner } from '@/components/ui/Spinner';
import { setDisplayPreferences } from '@/lib/dates';
import { CommandPalette } from './CommandPalette';
import { ShortcutsDialog } from './ShortcutsDialog';
import { Sidebar } from './Sidebar';
import { TestModeBanner } from './TestModeBanner';
import { Topbar } from './Topbar';

/**
 * Two columns under the test-data band, on the page ground that carries the
 * ambient glow: the rail (clear glass) and the content column (the top bar
 * over the main panel). The main panel is the scroll container, so sticky
 * table headers pin to its top edge.
 */
export function AppShell() {
  const { isTest, switching, environment, user, tenant } = useSession();
  const prefs = usePreferences(user.id);
  // Instants render in the viewer's chosen zone (the tenant's by default).
  const display = resolveDisplay(prefs, tenant.time_zone);
  setDisplayPreferences(display);
  const navigation = useNavigation();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  useGlobalHotkeys();

  // The panel scrolls, not the window: reset it on page change.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0 });
  }, [location.pathname]);

  return (
    <div className="flex h-dvh flex-col" data-environment={environment}>
      <a
        href="#main"
        className="sr-only z-[200] rounded-md bg-primary px-3 py-1.5 text-on-primary focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        Skip to content
      </a>
      {isTest && <TestModeBanner />}
      <div className="flex min-h-0 flex-1">
        <Sidebar />
        <div className="flex min-w-0 flex-1 flex-col">
          <Topbar />
          <main
            id="main"
            ref={mainRef}
            tabIndex={-1}
            className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain outline-none"
          >
            {navigation.state === 'loading' && (
              <div aria-hidden className="sticky top-0 z-30 h-0">
                <div className="h-0.5 w-full origin-left animate-[progress_1.2s_ease-out_infinite] bg-primary" />
              </div>
            )}
            {switching && (
              <div className="absolute inset-0 z-40 flex animate-fade-in items-center justify-center bg-backdrop/40 backdrop-blur-[3px]">
                <div className="flex items-center gap-2 rounded-lg px-4 py-2.5 text-base shadow-float glass-pane">
                  <Spinner /> Switching…
                </div>
              </div>
            )}
            {/* Remounted when the display zone or clock changes, so every time re-renders. */}
            <RouteGate key={`${display.timeZone}|${String(display.hour12)}`} />
          </main>
        </div>
      </div>
      <CommandPalette />
      <ShortcutsDialog />
    </div>
  );
}
